import { useRef } from 'react'

/** Pointer travel (px) beyond which a press counts as a scroll/drag, not a tap. */
const DRAG_THRESHOLD_PX = 10

export interface PressWithoutDragHandlers {
	onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
	onPointerCancel: () => void
	onPointerLeave: () => void
	onClick: (event: React.MouseEvent<HTMLElement>) => void
}

/**
 * Activation guard for a large clickable surface containing horizontally
 * scrollable inner rails (the bento tiles): a touch/pen press that travels past
 * a threshold was a rail scroll, so the click is swallowed. Mouse presses never
 * arm the guard, keyboard activation is never suppressed, and aborted gestures
 * clear the stored origin. Spread the handlers onto the surface element.
 */
export const usePressWithoutDrag = (onActivate?: () => void): PressWithoutDragHandlers => {
	const origin = useRef<{ x: number; y: number } | null>(null)

	const clear = () => {
		origin.current = null
	}

	return {
		onPointerDown: event => {
			origin.current =
				event.pointerType === 'touch' || event.pointerType === 'pen'
					? { x: event.clientX, y: event.clientY }
					: null
		},
		onPointerCancel: clear,
		onPointerLeave: clear,
		onClick: event => {
			const start = origin.current
			origin.current = null
			// `detail === 0` → keyboard or assistive tech: never suppress.
			if (start && event.detail > 0) {
				const travelled = Math.hypot(event.clientX - start.x, event.clientY - start.y)
				if (travelled > DRAG_THRESHOLD_PX) {
					event.preventDefault()
					event.stopPropagation()
					return
				}
			}
			onActivate?.()
		},
	}
}
